/**
 * Parses the month/year encoded in the trailing "/MM/YYYY" segment of an invoice number
 * (the format produced by TravelCRM's InvoiceNumberingService.GenerateNextNumberAsync,
 * e.g. "FV/12/09/2026"). Returns null for any shape that doesn't match, including
 * correction-invoice numbers ("{original}-K{n}"), which have no "/MM/YYYY" suffix.
 */
export const parseInvoiceNumberMonthYear = (
  invoiceNumber: string | null | undefined
): { month: number; year: number } | null => {
  if (!invoiceNumber) {
    return null;
  }

  const parts = invoiceNumber.split('/');
  if (parts.length < 3) {
    return null;
  }

  const monthPart = parts[parts.length - 2];
  const yearPart = parts[parts.length - 1];

  if (!/^\d{2}$/.test(monthPart) || !/^\d{4}$/.test(yearPart)) {
    return null;
  }

  const month = Number(monthPart);
  const year = Number(yearPart);

  if (month < 1 || month > 12) {
    return null;
  }

  return { month, year };
};

/**
 * Returns true when the given issueDate ("YYYY-MM-DD") falls in a different month/year
 * than the one encoded in invoiceNumber. Fails closed to false (no drift) whenever either
 * side can't be confidently parsed, so an unrecognized number format never produces a
 * false warning.
 */
export const hasIssueDateMonthYearDrifted = (
  invoiceNumber: string | null | undefined,
  issueDateIso: string | null | undefined
): boolean => {
  const numberMonthYear = parseInvoiceNumberMonthYear(invoiceNumber);
  if (!numberMonthYear || !issueDateIso) {
    return false;
  }

  const issueDateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(issueDateIso);
  if (!issueDateMatch) {
    return false;
  }

  const issueDateYear = Number(issueDateMatch[1]);
  const issueDateMonth = Number(issueDateMatch[2]);

  return issueDateMonth !== numberMonthYear.month || issueDateYear !== numberMonthYear.year;
};
